import { createRef, FocusEventHandler, KeyboardEventHandler, MouseEventHandler, RefObject } from "react";

import {
    ControlDisplayedValue,
    ControlValue,
    DataAttributesProps,
    EditableControlComponentProps,
    FAKE_COMPONENT_VALUE_LOC_KEY,
    OnEditableControlMount,
    OnValidationCallback,
    OnValidityChanged,
} from "@multiweb/reactComponents";
import { GenericControlConnectorAttrs } from "@multiweb/reactConnectors/GenericControlConnector";
import {
    ControlReduxRawValue,
    ControlsKey,
    ControlsValueInstantValidationMode,
    createClearControlsValueAction,
    createClearValidationErrorsAction,
    createControlsValueChangedAction,
    createInitControlsValueAction,
    createStableGetMergedValidationErrors,
    createValidateAction,
    getRawValue,
    IExplicitValidationError,
    ILocalizedExplicitValidationError,
    REQUIRED_INVALID_STATE_LOC_KEY,
} from "@multiweb/reactControls";
import { bridge } from "@multiweb/reactCore/bridge";
import { loadLocalizationIfNeeded } from "@multiweb/reactCore/i18n";
import { EMPTY_ARRAY, invariant, isMultiEditEraseKey } from "@multiweb/reactUtils";
import { OWDataType } from "@multiweb/types/contracts";
import { MutableTypedObject, TypedObject } from "@multiweb/types/react";
import { ITextBuilder } from "Global/Base/Builders/ITextBuilder";
import { Event } from "Global/Base/Events";
import { ValidationErrorType } from "OW.Remoting/Basic/EOWExceptionsInterfaces";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { IValidatable, IValidationResult } from "UI/UI.BaseControls/ControlInterfaces";
import { IEditableControl, IMultiEdit } from "UI/UI/UI/Controls/ControlInterfaces";
import { ControlUtils } from "UI/UI/UI/Controls/ControlUtils";
import { IControlInfoObject } from "UI/UI/UI/XGControls/ControlInfo";
import { ControlConnectorWithActualObject } from "../ControlConnectorWithActualObject";
import { RawAndDisplayedValue } from "./RawAndDisplayedValue";

const { dispatchAction, generateUUID, getState } = bridge;

/** Prefix použitý při tvorbě dočasného identifikátoru instance. */
const TEMP_DATA_KEY_PREFIX = "editableControlConnector_";

/**
 * Umožňuje napojení react komponent do původní multiweb aplikace.
 * Do konektoru je skrze konstruktor vložena react komponenta, které jsou následně předány props důležité pro její vykreslování.
 * Konektor implementuje stejná rozhraní jako editovatelný control {@link EditableControl} a zastupuje jeho funkci.
 * Konektor obsahuje dva typy IDček viz {@link __dataKey} a {@link GenericControlConnector#_componentId|_componentId}.
 */
export class EditableControlConnector extends ControlConnectorWithActualObject implements IEditableControl, IMultiEdit {
    /**
     * Identifikátor, který slouží jako klíč sloužící pro interakci s reduxem.
     * V případě, že nebyl vybrán záznam, použije se dočasný `dataKey`. Více info v `connectors.md`.
     * @protected
     */
    protected __dataKey: ControlsKey;
    /** Událost pro zasílání změn hodnoty enabled */
    protected _enabledChanged: Event<IEditableControl, any> = new Event<IEditableControl, any>();
    /** Událost pro zasílání změn hodnoty isEditing */
    protected _isEditingChanged: Event<IEditableControl, any> = new Event<IEditableControl, any>();
    /** Událost pro zasílání změn hodnoty komponenty */
    protected _valueChanged: Event<IEditableControl, any> = new Event<IEditableControl, any>();
    /** Událost pro zasílání změny v zaměření */
    protected _getFocus: Event<IEditableControl, FocusEvent> = new Event<IEditableControl, FocusEvent>();
    /** Událost odesílaná při ztrátě zaměření */
    protected _lostFocus: Event<IEditableControl, FocusEvent> = new Event<IEditableControl, FocusEvent>();
    /** Událost odesílaná při kliku na komponentu */
    protected _clicked: Event<IEditableControl, MouseEvent> = new Event<IEditableControl, MouseEvent>();
    /** Událost odesílaná při přejetí kurzorem přes komponentu */
    protected _hover: Event<IEditableControl, MouseEvent> = new Event<IEditableControl, MouseEvent>();
    /** Událost odesílaná v momentě, kdy kurzor vjede dovnitř oblasti komponenty */
    protected _mouseEnter: Event<IEditableControl, MouseEvent> = new Event<IEditableControl, MouseEvent>();
    /** Událost odesílaná v momentě, kdy kurzor vyjede ven z komponenty */
    protected _mouseLeave: Event<IEditableControl, MouseEvent> = new Event<IEditableControl, MouseEvent>();
    /** Událost odesílaná při změně hodnoty validace */
    protected _validityChanged: Event<IValidatable, IValidationResult> = new Event<IValidatable, IValidationResult>();

    /** Množina nainicializovaných klíčů tímto konektorem v reduxu */
    protected readonly _dataKeys: Set<ControlsKey>;
    /** Název zobrazeného atributu */
    protected _attrName: string;
    /** Typ hodnoty komponenty */
    protected _dataType: OWDataType | string;
    /** Příznak, zda je komponenta aktuálně editovaná */
    protected _isEditing: boolean = false;
    /** `True`, pokud se má zobrazit tooltip s chybovou hláškou, že pole je povinné a nemá vyplněnu (validní) hodnotu. */
    protected _isInRequiredInvalidState: boolean = false;
    /** Obsahuje hodnotu zda uz byla nactena nebo zmenena hodnota nebo ne (kvuli zmene viditelnosti) */
    protected _isValueLoaded = false;
    /** Příznak, zda je vyplnění hodnoty komponenty odloženo */
    protected _postponedRequired = false;
    /** Příznak, zda je vyplnění hodnoty komponenty doporučeno */
    protected _recommended = false;
    /** Příznak, zda je vyplnění hodnoty komponenty vyžadováno */
    protected _required = false;
    /** Příznak, zda je aktivní hromadná editace */
    protected _multiEditActive: boolean = false;
    /**
     * Příznak, zda proběhla interakce s fake komponentou v rámci jednoho cyklu hromadné editace.
     * (vypnutí hromadné editace příznak resetuje)
     */
    protected __touched: boolean = false;
    /** Reference na vstupni pole. */
    private readonly _inputRef: RefObject<HTMLElement>;
    /** Chyby získané přes volání {@link setInvalid}. */
    private _externalErrors: ReadonlyArray<IExplicitValidationError> = EMPTY_ARRAY;
    /**
     * Selektor, viz {@link createStableGetMergedValidationErrors}.
     * Slouží pouze pro implementaci metody {@link _getMergedValidationErrors}.
     */
    private __getMergedValidationErrors = createStableGetMergedValidationErrors();
    /** Převádí rawValue na {@link ControlDisplayedValue}. */
    private _getDisplayedValue?: (rawValue: ControlReduxRawValue) => ControlDisplayedValue;
    /** Převádí rawValue na {@link ControlValue}. */
    private _getControlValue?: (rawValue: ControlReduxRawValue) => ControlValue;
    /** Převádí {@link ControlValue} na rawValue. */
    private _convertControlValueToRawValue?: (controlValue: ControlValue) => ControlReduxRawValue;

    /**
     * Inicializuje listenery a ve stavu pod klíčem {@link __dataKey} nainicializuje data potřebná pro control.
     * @param attrs Hlavní atributy konektoru
     * @param canRender Příznak, zda se může komponenta vykreslit hned po inicializaci konektoru.
     * Ve výchozím nastavení (`true`) je komponenta vykreslena po volání {@link _renderIfTrue} v konstruktoru
     */
    constructor(attrs: GenericControlConnectorAttrs, canRender: boolean = true) {
        super(attrs, false);
        this._inputRef = createRef<HTMLElement>();
        this._initializeListeners();

        this.element.classList.add("EditableControlConnector");

        /* Tvorba dočasného `__dataKey`. Více informací v connectors.md */
        this.__dataKey = TEMP_DATA_KEY_PREFIX + generateUUID();
        this._dataKeys = new Set([this.__dataKey]);
        dispatchAction(createInitControlsValueAction(this.dataKey, null));
        this._renderIfTrue(canRender);
    }

    private _initializeListeners(): void {
        this.valueChanged?.add(this._valueChangedListener);
        this.canValueLoadChanged?.add(this._canValueLoadChangedListener);
        this.isEditingChanged?.add(this._isEditingChangedListener);
    }

    /**
     * Listener pro událost {@link valueChanged}.
     * Volá se při opuštění pole se změněnou hodnotou.
     * @see {@link _triggerValueChange}
     * @private
     */
    private _valueChangedListener = () => {
        this.isValueLoaded = true;
    };

    private _canValueLoadChangedListener = (_sender: EditableControlConnector, value: boolean) => {
        this.valueChanged.enabled = value;
    };

    /**
     * Listener volaný při změně editace.
     * Pokud byla ukončena editace, odstraní nepoužívané data pro pole se zadaným klíčem.
     * @param _sender Objekt, na kterém byla událost vyvolaná
     * @param value Hodnota, zda je aktivní editace.
     * @private
     */
    private _isEditingChangedListener = (_sender: EditableControlConnector, { value }: {value: boolean}) => {
        if (value === false) {
            this._disposeUnusedControlsValues();
        }
    };

    public get dataKey(): string {
        return this.__dataKey;
    }

    protected set _dataKey(dataKey: ControlsKey) {
        this._initControlsValue(dataKey);
        this.__dataKey = dataKey;
        this._render();
    }

    /**
     * Na konektoru vytvoří nový {@link EditableControlConnector#__dataKey|dataKey}.
     * Pro každý záznam nainicializuje data v reduxu.
     * Pokud nebyl předešlý záznam editován, odstraní data z reduxu.
     * V případě, že je záznam nedefinovaný, nastaví dočasný klíč.
     * @param actualObject
     */
    public override set actualObject(actualObject: OWObject | null) {
        const previousDataKey = this.dataKey;
        const previousObjectChanged = this._actualObject?.isChanged || false;
        super.actualObject = actualObject;
        if (actualObject) {
            this._dataKey = actualObject.instanceID + "_" + this.attrName;
        } else {
            this._dataKey = TEMP_DATA_KEY_PREFIX + generateUUID();
        }
        if (!previousObjectChanged && previousDataKey !== this.dataKey) {
            this._disposeControlsValue(previousDataKey);
        }
    }

    /**
     * Inicializuje novou hodnotu v reduxu pod zadaným klíčem.
     * Pokud byla hodnota tímto konektorem již nainicializovaná, nedělá nic.
     * @param dataKey Klíč pod kterým jsou uložena data.
     * @private
     */
    private _initControlsValue(dataKey: ControlsKey): void {
        if (!this._dataKeys.has(dataKey)) {
            dispatchAction(createInitControlsValueAction(dataKey, null));
            this._dataKeys.add(dataKey);
        }
    }

    /**
     * Odstraní data pod daným klíčem z reduxu.
     * Akce se provede pouze v případě, že konektor nainiciaizoval hodnotu pod zadaným klíčem.
     * @param dataKey Klíč pod kterým jsou uložena data.
     * @private
     */
    private _disposeControlsValue(dataKey: ControlsKey): void {
        if (this._dataKeys.has(dataKey)) {
            dispatchAction(createClearControlsValueAction(dataKey));
            this._dataKeys.delete(dataKey);
        }
    }

    /**
     * Pro každý klíč kromě aktuálně používaného {@link EditableControlConnector#__dataKey|dataKey} odstraní data z reduxu.
     * Tento klíč je zároveň odstraněn z množiny nainicializovaných klíčů {@link EditableControlConnector#_dataKeys|_dataKeys}.
     * @private
     */
    private _disposeUnusedControlsValues(): void {
        this._dataKeys.forEach(dataKey => {
            if (dataKey !== this.dataKey) {
                dispatchAction(createClearControlsValueAction(dataKey));
                this._dataKeys.delete(dataKey);
            }
        });
    }

    /**
     * Vrátí objekt s props pro react komponentu. Objekt obsahuje pouze props primitivního typu.
     * Hodnoty jsou převedeny na string. Používá se v metodě {@link getControlInfo}.
     * @private
     */
    private _getRenderPropsAttributes(): TypedObject<string> {
        return Object.entries(this._getRenderProps())
            .reduce((attributes, [key, value]) => {
                // Pokud je hodnota primitivní, přidá ji k atributům
                if (Object(value) !== value) {
                    attributes[key] = String(value);
                }
                return attributes;
            }, {} as MutableTypedObject<string>);
    }

    public get enabledChanged(): Event<IEditableControl, any> {
        return this._enabledChanged;
    }

    public set enabledChanged(value: Event<IEditableControl, any>) {
        this._enabledChanged = value;
    }

    public override set enabled(value: boolean) {
        if (this._enabled !== value) {
            this.enabledChanged.trigger(this, { value: value });
        }
        super.enabled = value;
    }

    public override get enabled(): boolean {
        return this._enabled;
    }

    public get isEditingChanged(): Event<IEditableControl, any> {
        return this._isEditingChanged;
    }

    public set isEditingChanged(value: Event<IEditableControl, any>) {
        this._isEditingChanged = value;
    }

    public get valueChanged(): Event<IEditableControl, any> {
        return this._valueChanged;
    }

    public set valueChanged(value: Event<IEditableControl, any>) {
        this._valueChanged = value;
        this._render();
    }

    public get getFocus(): Event<IEditableControl, FocusEvent> {
        return this._getFocus;
    }

    public set getFocus(value: Event<IEditableControl, FocusEvent>) {
        this._getFocus = value;
        this._render();
    }

    public get lostFocus(): Event<IEditableControl, FocusEvent> {
        return this._lostFocus;
    }

    public set lostFocus(value: Event<IEditableControl, FocusEvent>) {
        this._lostFocus = value;
        this._render();
    }

    public get clicked(): Event<IEditableControl, MouseEvent> {
        return this._clicked;
    }

    public set clicked(value: Event<IEditableControl, MouseEvent>) {
        this._clicked = value;
        this._render();
    }

    public get hover(): Event<IEditableControl, MouseEvent> {
        return this._hover;
    }

    public set hover(value: Event<IEditableControl, MouseEvent>) {
        this._hover = value;
        this._render();
    }

    public get mouseEnter(): Event<IEditableControl, MouseEvent> {
        return this._mouseEnter;
    }

    public set mouseEnter(value: Event<IEditableControl, MouseEvent>) {
        this._mouseEnter = value;
        this._render();
    }

    public get mouseLeave(): Event<IEditableControl, MouseEvent> {
        return this._mouseLeave;
    }

    public set mouseLeave(value: Event<IEditableControl, MouseEvent>) {
        this._mouseLeave = value;
        this._render();
    }

    public get validityChanged(): Event<IValidatable, IValidationResult> {
        return this._validityChanged;
    }

    public set validityChanged(value: Event<IValidatable, IValidationResult>) {
        this._validityChanged = value;
    }

    public get attrName(): string {
        return this._attrName;
    }

    public set attrName(value: string) {
        this._attrName = value;
    }

    public override get visible(): boolean {
        return super.visible;
    }

    /**
     * Nastaví viditelnost controlu.
     * Funkcionalita převzata z {@link EditableControl.visible}.
     */
    public override set visible(value: boolean) {
        const oldVal = this._visible;
        super.visible = value;

        if (oldVal === value) {
            // Pokud nedošlo ke změně viditelnosti, mohlo dojít ke změně canValueLoad, díky změně viditelnosti předka v DOM.
            this.tryTriggerCanValueLoadChange(this, this.canValueLoad);
        }
    }

    public setInitialControlValue(value: ControlValue): void {
        this._setControlValue(value, ControlsValueInstantValidationMode.initialValue);
    }

    public override get controlValue(): ControlValue {
        invariant(!!this._getControlValue, "_getControlValue is not set yet");
        return this._getControlValue(this._rawValue);
    }

    /**
     * @inheritDoc
     * Při nastavení hodnoty se smažou externí validační chyby.
     * Chyby je nutné smazat kvůli případu, kdy existuje více konektorů pod shodným {@link __dataKey}
     * a v některém z nich se změní hodnota.
     * Bez smazání by komponenty v zbývajících konektorech zůstaly označené jako nevalidní.
     *
     * @param value Hodnota která se nastaví.
     */
    public override set controlValue(value: ControlValue) {
        /**Pokud se jedná o dočasný identifikátor instance, který indikuje žádný vybraný objekt, nesmí se
         * nastavovat hodnota.
         * Docházelo k tomu v situaci, kdy založím záznam, vyplním pole a ihned kliknu na zahodit změny.
         * OnBlur událost vyvolala update instance atributu, v mezi čase zpracování update došlo k zrušení nového objektu
         * a původní hodnota se pak zapsala do redux do tohoto dočasného záznamu.
         */
        if (this.dataKey.indexOf(TEMP_DATA_KEY_PREFIX) === -1) {
            this._touched = true;
            this._resetExternalErrors();
            this._setControlValue(value, ControlsValueInstantValidationMode.allowInvalid);
        }
    }

    private _setControlValue(value: ControlValue, validationMode: ControlsValueInstantValidationMode): void {
        invariant(!!this._convertControlValueToRawValue, "_convertControlValueToRawValue is not set yet");
        const rawValue = this._convertControlValueToRawValue(value);
        dispatchAction(createControlsValueChangedAction(this.dataKey, rawValue, validationMode));
        this.validate();
    }

    /**
     * Vrací všechny validační chyby. Tj. sloučený seznam chyb z Reduxu a externích chyb.
     * Chyby vrací lokalizované.
     * @private
     */
    private _getMergedValidationErrors(): ReadonlyArray<ILocalizedExplicitValidationError> {
        return this.__getMergedValidationErrors(getState(), { dataKey: this.__dataKey, externalErrors: this._externalErrors });
    }

    /**
     * Smaže externí validační chyby.
     * @private
     */
    private _resetExternalErrors(): void {
        this._externalErrors = EMPTY_ARRAY;
        this._render();
    }

    public get currentValidity(): IValidationResult {
        const mergedErrors = this._getMergedValidationErrors();

        // Přestože isInRequiredInvalidState se nepovažuje za validační chybu (není předmětem události validityChanged),
        // tak je žádoucí ji zohlednit do currentValidity.
        if (this.isInRequiredInvalidState) {
            const invalidValue = this.displayedValue;
            const invalidLocKey = REQUIRED_INVALID_STATE_LOC_KEY;

            return { valid: false, errorType: null, invalidValue, invalidLocKey };
        }

        if (mergedErrors.length === 0) {
            return { valid: true };
        } else {
            const firstError = mergedErrors[0];
            const invalidValue = this.displayedValue;
            const invalidMessage = firstError.message;
            const errorType = this._externalErrors[0]?.errorType;

            return { valid: false, invalidValue, invalidMessage, errorType };
        }
    }

    public get dataType(): OWDataType | string {
        return this._dataType;
    }

    public set dataType(value: OWDataType | string) {
        this._dataType = value;
    }

    /**
     * `displayedValue` se používá pro dva usecases:
     * - Jako textová podoba daného control - typicky pro titulek formulářového okna (`DetailFrame._captionAttrs`).
     * - Jako raw hodnota daného control - typicky pro potřeby jejího uchování (např. ve ValidationManageru) za účelem pozdější obnovy nebo duplikace do jiných controls.
     */
    public get displayedValue(): ITextBuilder /*RawAndDisplayedValue*/ {
        invariant(!!this._getDisplayedValue, "_getDisplayedValue is not set yet");

        const rawValue = this._rawValue;
        const displayedValue = this._getDisplayedValue(rawValue);
        return new RawAndDisplayedValue(rawValue, displayedValue);
    }

    /**
     * Viz getter {@link displayedValue}, usecase _"jako raw hodnota"_.
     */
    public set displayedValue(rawAndDisplayedValue: ITextBuilder /*RawAndDisplayedValue*/) {
        invariant(rawAndDisplayedValue instanceof RawAndDisplayedValue, "rawAndDisplayedValue is not instance of RawAndDisplayedValue");
        dispatchAction(createControlsValueChangedAction(this.dataKey, rawAndDisplayedValue.rawValue, ControlsValueInstantValidationMode.allowInvalid));
        this.validate();
    }

    private get _rawValue(): ControlReduxRawValue {
        return getRawValue(getState(), this.dataKey);
    }

    /**
     * `innerElement` je zastupitelný React komponentou.
     * @throws Volání getteru vždy vyhodí výjimku.
     */
    public get innerElement(): never {
        throw new Error("[EditableControlConnector] Unsupported operation");
    }

    public get isEditing(): boolean {
        return this._isEditing;
    }

    public set isEditing(value: boolean) {
        const oldValue = this._isEditing;
        this._isEditing = value;

        if (oldValue !== value) {
            this.isEditingChanged.trigger(this, { value });
            if (!this.isEditing) {
                this._touched = false;
            }
            if (value && this.isInRequiredInvalidState) { // Pokud byl přepnut stav povinnosti mimo editaci (třeba přes posponeRequired), tak je potřeba po zahájení editace zavolat znovu validaci.
                this.validate();
            }
        }
    }

    public get isInRequiredInvalidState(): boolean {
        return this._isInRequiredInvalidState;
    }

    public set isInRequiredInvalidState(value: boolean) {
        if (value !== this._isInRequiredInvalidState) {
            this._isInRequiredInvalidState = value;
            this._render();
        }
    }

    public get isValid(): boolean {
        return this.currentValidity.valid;
    }

    public get isValueLoaded(): boolean {
        return this._isValueLoaded;
    }

    public set isValueLoaded(value: boolean) {
        this._isValueLoaded = value;
    }

    public get postponedRequired(): boolean {
        return this._postponedRequired;
    }

    public set postponedRequired(value: boolean) {
        if (this._postponedRequired !== value) {
            this._postponedRequired = value;

            if (this._captionLabel) {
                this._captionLabel.classList.toggle("PostponedRequired", value);
            }

            this._render();
        }
    }

    public get recommended(): boolean {
        return this._recommended;
    }

    public set recommended(value: boolean) {
        if (this._recommended !== value) {
            this._recommended = value;

            if (this._captionLabel) {
                this._captionLabel.classList.toggle("Recommended", value);
            }

            this._render();
        }
    }

    public get required(): boolean {
        return this._required;
    }

    public set required(value: boolean) {
        if (this._required !== value) {
            this._required = value;
            this.validate();

            if (this._captionLabel) {
                this._captionLabel.classList.toggle("Required", value);
            }

            this._handleValidityChanged();
            this._render();
        }
    }

    public focus(): void {
        this._inputRef.current?.focus();
        this.isInRequiredInvalidState = false;
    }

    public async getControlInfo(childInfo: Partial<IControlInfoObject> | null): Promise<Partial<IControlInfoObject>> {
        return {
            ...childInfo,
            attributeName: this.attrName,
            locKey: this.locKey,
            additionalInfo: {
                dataAttributes: {
                    ...this._getDataAttributes(),
                    ...this._getRenderPropsAttributes(),
                },
                implementationClass: this.constructor.name,
            },
        };
    }

    /**
     * Kontrola, jestli je vyplněné postponed pole
     */
    public isFilledPostponedControl(): boolean {
        const isFilledPostponedControl = !(this.postponedRequired && !this.controlValue && this.visible);

        if (this.multiEditActive) {
            return !this.touched || isFilledPostponedControl;
        }

        return isFilledPostponedControl;
    }

    /**
     * Kontrola, jestli je vyplněné doporučené pole
     */
    public isFilledRecommendedControl(): boolean {
        const isFilledRecommendedControl = !(this.recommended && !this.controlValue);

        if (this.multiEditActive) {
            return !this.touched || isFilledRecommendedControl;
        }

        return isFilledRecommendedControl;
    }

    /**
     * Kontrola, jestli je vyplněné povinné pole
     */
    public isFilledRequiredControl(): boolean {
        const isFilledRequiredControl = !(this.required && !this.controlValue);

        if (this.multiEditActive) {
            return !this.touched || isFilledRequiredControl;
        }

        return isFilledRequiredControl;
    }

    public setInvalid(locKey: string, message?: string, errorType?: ValidationErrorType): void {
        this._externalErrors = [{ locKey, message, errorType }];
        this._render();
    }

    public setValid(): void {
        this._externalErrors = EMPTY_ARRAY;
        this._isInRequiredInvalidState = false;
        dispatchAction(createClearValidationErrorsAction(this.__dataKey));
        this._render();
    }

    public get multiEditActive(): boolean {
        return this._multiEditActive;
    }

    public set multiEditActive(value) {
        if (this._multiEditActive !== value) {
            this._multiEditActive = value;
            if (this._multiEditActive) {
                loadLocalizationIfNeeded(FAKE_COMPONENT_VALUE_LOC_KEY);
            } else {
                this._touched = false;
            }
            this._render();
        }
    }

    public get touched(): boolean {
        return this._touched;
    }

    /**
     * Popis viz {@link __touched}.
     */
    protected get _touched(): boolean {
        return this.__touched;
    }

    /**
     * Nastaví příznak, zda proběhla interakce s fake komponentou.
     * @param value Hodnota true se nastaví pouze při aktivní hromadné editaci.
     */
    protected set _touched(value: boolean) {
        const oldTouched = this.__touched;
        this.__touched = this.multiEditActive && value;
        if (this.__touched !== oldTouched) {
            this._render();
        }
    }

    /**
     * @inheritDoc
     */
    public validate(): boolean {
        this._handleValidationCallback();
        dispatchAction(createValidateAction(this.__dataKey));
        return this.isValid;
    }

    /**
     * Handler obstarávající callback {@link OnValidationCallback} vyvolaný tímto control:
     * - buď komponentou {@link _component} (typicky na `onBlur`)
     * - nebo metodou {@link validate}
     */
    private _handleValidationCallback: OnValidationCallback = () => {
        // Spuštění validace musí vynutit shození příznaku isInRequiredInvalidState.
        // - Když validace proběhne úspěšně, pak uživatel nesmí mít žádné chybové tooltipy ani červené podbarvení.
        // - A když z validace vypadnou validační chyby, pak je žádoucí, aby uživatel viděl tyto validační chyby. A nikoli hlášku o tom, že povinné pole není vyplněné.
        this.isInRequiredInvalidState = false;
    };

    /**
     * Handler obstarávající událost {@link OnValidityChanged} vyvolanou komponentou {@link _component}.
     * Zajišťuje provolání události {@link _validityChanged}.
     */
    private _handleValidityChanged: OnValidityChanged = () => {
        this._validityChanged.trigger(this, this.currentValidity);
    };

    private _clearIsInRequiredInvalidStateIfValueNotEmpty(): void {
        if (this.controlValue) {
            this.isInRequiredInvalidState = false;
        }
    }

    /**
     * Po úspěšné validaci vyvolá událost {@link _valueChanged}.
     * @see {@link EditableControlComponentProps.triggerValueChange}
     */
    protected _triggerValueChange = () => {
        this.validate();
        this.valueChanged.trigger(this, { Sender: this });
    };

    private _handleMount: OnEditableControlMount = ({ getDisplayedValue, getControlValue, convertControlValueToRawValue }) => {
        this._getDisplayedValue = getDisplayedValue;
        this._getControlValue = getControlValue;
        this._convertControlValueToRawValue = convertControlValueToRawValue;
    };

    private _handleBlur: FocusEventHandler = (e) => {
        this._clearIsInRequiredInvalidStateIfValueNotEmpty();
        this.lostFocus.trigger(this, e.nativeEvent);
    };

    private _handleFocus: FocusEventHandler = (e) => {
        this.getFocus.trigger(this, e.nativeEvent);
    };

    private _handleMouseOver: MouseEventHandler = (e) => {
        this.hover.trigger(this, e.nativeEvent);
    };

    private _handleMouseEnter: MouseEventHandler = (e) => {
        this.mouseEnter.trigger(this, e.nativeEvent);
    };

    private _handleMouseLeave: MouseEventHandler = (e) => {
        this.mouseLeave.trigger(this, e.nativeEvent);
    };

    /**
     * Handler obstarávající událost kliknutí na komponentu.
     * Pokud je aktivní hromadná selekce, kliknutí na komponentu spustí editaci.
     * Další kliknutí (pokud se nejedná o CheckBoxControlConnector) neprovádí nic.
     *
     * @param e Událost.
     */
    protected _handleClick: MouseEventHandler = (e) => {
        if (this.multiEditActive && this.enabledVisual) {
            return;
        } else if (!this.multiEditActive || this.enabledVisual) {
            this._touched = true;
        }

        this.clicked.trigger(this, e.nativeEvent);
    };

    private _handleKeyDown: KeyboardEventHandler = (e) => {
        if (isMultiEditEraseKey(e)) {
            this._touched = true;
            this._triggerValueChange();
        }
    };

    /**
     * Vrací objekt s props ze zparsovaných data-atributů z rootElementu.
     * @private
     */
    protected _getPropsFromDataAttributes(): DataAttributesProps {
        return {
            maxLength: ControlUtils.getNumDataAttr("maxlength", this.element),
            minLength: ControlUtils.getNumDataAttr("minlength", this.element),
            validationRegex: ControlUtils.getDataAttr("validationexpr", this.element),
            displayFormat: ControlUtils.getDataAttr("displayformat", this.element),
            captionLocKey: ControlUtils.getDataAttr("captionlockey", this.element),
        };
    }

    /**
     * Výchozí implementace pro zahájení editace pomocí zaměření komponenty.
     *
     * @see IStartEdit
     */
    public startEdit(): void {
        this.focus();
    }

    /**
     * Zkonvertuje atributy ovlivňující podobu editovatelné vykreslované react komponenty do props.
     * Přidává i zparsované hodnoty z data atributů rootElementu.
     * @private
     */
    protected override _getRenderProps(): EditableControlComponentProps & DataAttributesProps {
        return {
            ...super._getRenderProps(),
            ...this._getPropsFromDataAttributes(),
            dataKey: this.dataKey,
            postponedRequired: this.postponedRequired,
            enabledVisual: this.enabledVisual,
            recommended: this.recommended,
            required: this.required,
            isEditing: this.isEditing,
            ref: this._inputRef,
            externalErrors: this._externalErrors,
            isInRequiredInvalidState: this._isInRequiredInvalidState,
            onValidationCallback: this._handleValidationCallback,
            onValidityChanged: this._handleValidityChanged,
            onMount: this._handleMount,
            onFocus: this._handleFocus,
            onMouseOver: this._handleMouseOver,
            onMouseLeave: this._handleMouseLeave,
            onMouseEnter: this._handleMouseEnter,
            onKeyDown: this._handleKeyDown,
            onClick: this._handleClick,
            onBlur: this._handleBlur,
            triggerValueChange: this._triggerValueChange,
            multiEditActive: this.multiEditActive,
            touched: this.touched,
        };
    }

    /**
     * @inheritDoc
     */
    public override dispose(): void {
        this._enabledChanged.removeAll();
        this._isEditingChanged.removeAll();
        this._valueChanged.removeAll();
        this._getFocus.removeAll();
        this._lostFocus.removeAll();
        this._clicked.removeAll();
        this._hover.removeAll();
        this._mouseEnter.removeAll();
        this._mouseLeave.removeAll();
        this._validityChanged.removeAll();

        super.dispose();
        // redux je nutné uvolnit až poté, co již není zapojená komponenta, proto je až za super.dispose()
        this._disposeControlsValue(this.dataKey);
        this._disposeUnusedControlsValues();
    }
}
